import { ChangeDetectionStrategy, Component, inject, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { firstValueFrom, Subject } from 'rxjs';

import { BaseComponent } from '../../../../core/abstractions/base.component';
import { AlertTypeModel } from '../../../../models/alert-type.model';
import { AlertModel } from '../../../../models/alert.model';
import { ApiResponseModel } from '../../../../models/core/api-response.model';
import { SimpleItemModel } from '../../../../models/simple-item.model';
import { AlertsResource } from '../../../../services/resources/alerts.resource';
import { AlertEditDialogDataModel } from './models/alert-edit-dialog-data.model';

@Component({
    selector: 'sl-alert-edit-dialog',
    templateUrl: './alert-edit-dialog.component.html',
    styleUrls: ['./alert-edit-dialog.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class AlertEditDialogComponent extends BaseComponent implements OnInit {
    showLoadingProgressBar: boolean;
    alertTypes: AlertTypeModel[] = [];
    selectedAlertType?: AlertTypeModel;
    alertTypesSelectItemsObservable: Subject<ApiResponseModel<SimpleItemModel[]>> = new Subject();
    isEditing: boolean;
    alert: AlertModel;

    formGroup = new FormGroup({
        id: new FormControl(''),
        alertType: new FormControl('', Validators.required),
        customMessage: new FormControl(),
        parametersJson: new FormControl(),
        parameters: new FormGroup({})
    });

    private readonly data = inject<AlertEditDialogDataModel>(MAT_DIALOG_DATA);
    private readonly dialogRef = inject(MatDialogRef);
    private readonly alertsResource = inject(AlertsResource);

    async ngOnInit(): Promise<void> {
        this.showLoadingProgressBar = true;
        this.isEditing = this.data?.isEditing === true;

        this.initForm();

        this.alertTypes = (await firstValueFrom(this.alertsResource.getAlertTypes())).value;
        this.alertTypesSelectItemsObservable.next(
            new ApiResponseModel({
                value: this.alertTypes.map(a => new SimpleItemModel({ id: a.className, label: a.displayName }))
            })
        );

        if (this.data.isEditing && !!this.data.alertId) {
            this.alert = (await firstValueFrom(this.alertsResource.get(this.data.alertId.toString()))).value;
            this.formGroup.patchValue(this.alert);
        }

        this.showLoadingProgressBar = false;
    }

    save(): void {
        this.formGroup.updateValueAndValidity();
        this.formGroup.markAllAsTouched();
        if (this.formGroup.invalid) {
            return;
        }

        const alert = this.formGroup.getRawValue() as AlertModel;
        alert.parametersJson = JSON.stringify(this.formGroup.value.parameters);

        if (this.isEditing && !!this.data?.alertId) {
            // update
            alert.id = this.data.alertId.toString();
            this.alertsResource.edit(alert).subscribe(result => this.closeIfSuccessful(result.value.isSuccessful));
        } else {
            // create
            this.alertsResource.add(alert).subscribe(result => this.closeIfSuccessful(result.value.isSuccessful));
        }
    }

    delete(): void {
        if (this.isEditing && !!this.data?.alertId) {
            this.alertsResource.remove(this.data.alertId.toString()).subscribe(result => this.closeIfSuccessful(result.value));
        }
    }

    private initForm(): void {
        // value change handlers
        this.formGroup.controls.alertType.valueChanges.subscribe(val => {
            this.selectedAlertType = this.alertTypes.find(a => a.className === val)!;
        });
    }

    private closeIfSuccessful(successful: boolean): void {
        if (successful) {
            this.dialogRef.close();
        }
    }
}
